import type { SimulationSnapshot, VisualEvent } from '../../game/simulation/types';

export type AudioCue =
  | 'error'
  | 'aa-shot'
  | 'big-explosion'
  | 'chain-shot'
  | 'construction'
  | 'gauss-shot'
  | 'goo-shot'
  | 'gatling-shot'
  | 'hit'
  | 'medium-explosion'
  | 'missile-shot'
  | 'mortar-shot'
  | 'nuclear-explosion'
  | 'nuclear-launch'
  | 'nulificator-shot'
  | 'oil-shot'
  | 'pop-dead'
  | 'sell'
  | 'small-explosion'
  | 'switch'
  | 'tesla-shot'
  | 'time-stop'
  | 'quest';

export interface AudioSettings {
  musicMuted: boolean;
  sfxMuted: boolean;
}

const AUDIO_STORAGE_KEY = 'cd1.audio-settings.v1';
const AUDIO_ROOT = 'assets/audio/cd1-original';
const MUSIC_PATH = `${AUDIO_ROOT}/681_snd_mainLoop.mp3`;

const CUE_PATHS: Readonly<Record<AudioCue, string>> = {
  error: `${AUDIO_ROOT}/1_snd_error.wav`,
  'aa-shot': `${AUDIO_ROOT}/673_snd_aaGunShot.mp3`,
  'big-explosion': `${AUDIO_ROOT}/674_snd_bigExplosion.mp3`,
  'chain-shot': `${AUDIO_ROOT}/675_snd_chainGunShot.mp3`,
  construction: `${AUDIO_ROOT}/676_snd_construction.mp3`,
  'gauss-shot': `${AUDIO_ROOT}/677_snd_gaussCannonShot.mp3`,
  'goo-shot': `${AUDIO_ROOT}/678_snd_gooCannonShot.mp3`,
  'gatling-shot': `${AUDIO_ROOT}/679_snd_gutlingLaserShot.mp3`,
  hit: `${AUDIO_ROOT}/680_snd_hit.mp3`,
  'medium-explosion': `${AUDIO_ROOT}/682_snd_mediumExplosion.mp3`,
  'missile-shot': `${AUDIO_ROOT}/683_snd_missileTurretShot.mp3`,
  'mortar-shot': `${AUDIO_ROOT}/684_snd_mortarShot.mp3`,
  'nuclear-explosion': `${AUDIO_ROOT}/685_snd_nuclearExplosion.wav`,
  'nuclear-launch': `${AUDIO_ROOT}/686_snd_nuclearLaunch.mp3`,
  'nulificator-shot': `${AUDIO_ROOT}/687_snd_NulificatorShot.mp3`,
  'oil-shot': `${AUDIO_ROOT}/688_snd_oilSprayShot.mp3`,
  'pop-dead': `${AUDIO_ROOT}/689_snd_popDead.mp3`,
  sell: `${AUDIO_ROOT}/690_snd_sell.mp3`,
  'small-explosion': `${AUDIO_ROOT}/691_snd_smallExplosion.mp3`,
  switch: `${AUDIO_ROOT}/692_snd_switch.mp3`,
  'tesla-shot': `${AUDIO_ROOT}/693_snd_teslaTowerShot.mp3`,
  'time-stop': `${AUDIO_ROOT}/694_snd_timeStop.mp3`,
  quest: `${AUDIO_ROOT}/695_snd_quest.mp3`,
};

const PROJECTILE_CUES: Readonly<Record<string, AudioCue>> = {
  rocket: 'missile-shot',
  ball: 'mortar-shot',
  electricField: 'tesla-shot',
  laserBeam: 'gatling-shot',
  nulifyBall: 'nulificator-shot',
  aaShell: 'aa-shot',
  slowShot: 'goo-shot',
  chainBlast: 'chain-shot',
  gaussRail: 'gauss-shot',
  confuseBall: 'oil-shot',
};

const CUE_COOLDOWNS: Readonly<Partial<Record<AudioCue, number>>> = {
  'aa-shot': 85,
  'chain-shot': 120,
  'gauss-shot': 140,
  'goo-shot': 130,
  'gatling-shot': 75,
  hit: 100,
  'missile-shot': 150,
  'mortar-shot': 150,
  'nulificator-shot': 180,
  'oil-shot': 140,
  'small-explosion': 125,
  'medium-explosion': 180,
  'big-explosion': 350,
  'nuclear-explosion': 600,
  'pop-dead': 650,
  quest: 350,
  switch: 70,
};

const cueForImpact = (event: VisualEvent): AudioCue => {
  if (event.ability === 'missile-impact') return 'nuclear-explosion';
  if ((event.radius ?? 0) >= 60) return 'big-explosion';
  if (event.projectileKind === 'rocket' || event.projectileKind === 'ball') return 'medium-explosion';
  if (event.projectileKind === 'aaShell' || event.projectileKind === 'gaussRail') return 'small-explosion';
  return 'hit';
};

/** Maps deterministic renderer events to their recovered reference-SWF audio cues. */
export const cueForVisualEvent = (event: VisualEvent): AudioCue | null => {
  if (event.kind === 'fire') return event.projectileKind ? PROJECTILE_CUES[event.projectileKind] ?? null : null;
  if (event.kind === 'impact') return cueForImpact(event);
  if (event.kind !== 'ability') return null;
  if (event.ability === 'missile-cast') return 'nuclear-launch';
  if (event.ability === 'stop-time') return 'time-stop';
  return null;
};

const readSettings = (): AudioSettings => {
  try {
    const value = JSON.parse(window.localStorage.getItem(AUDIO_STORAGE_KEY) ?? '{}') as Partial<AudioSettings>;
    return { musicMuted: value.musicMuted === true, sfxMuted: value.sfxMuted === true };
  } catch {
    return { musicMuted: false, sfxMuted: false };
  }
};

/**
 * DOM audio layer for the Phaser/DOM hybrid. It observes simulation snapshots only; it never changes game state.
 * Browser playback is intentionally armed by an actual pointer/key gesture rather than attempted at page load.
 */
export class AudioDirector {
  private settings: AudioSettings = readSettings();
  private unlocked = false;
  private paused = false;
  private music: HTMLAudioElement | null = null;
  private activeOneShots = new Set<HTMLAudioElement>();
  private seenVisualEventIds = new Set<number>();
  private knownUnlocks: ReadonlySet<string> | null = null;
  private lastWaveIndex: number | null = null;
  private lastKillCount: number | null = null;
  private lastLives: number | null = null;
  private lastCueAt = new Map<AudioCue, number>();
  private documentWasHidden = false;

  private readonly onGesture = (): void => { this.unlock(); };
  private readonly onVisibilityChange = (): void => {
    if (document.hidden) {
      this.documentWasHidden = this.music !== null && !this.music.paused;
      this.music?.pause();
      this.stopOneShots();
    } else if (this.documentWasHidden) {
      this.documentWasHidden = false;
      this.syncMusic();
    }
  };

  arm(root: HTMLElement): void {
    root.addEventListener('pointerdown', this.onGesture);
    window.addEventListener('keydown', this.onGesture);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  destroy(root: HTMLElement | null): void {
    root?.removeEventListener('pointerdown', this.onGesture);
    window.removeEventListener('keydown', this.onGesture);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.stopOneShots();
    this.music?.pause();
    if (this.music) this.music.src = '';
    this.music = null;
    this.unlocked = false;
  }

  unlock(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    this.syncMusic();
  }

  beginRun(snapshot: SimulationSnapshot): void {
    this.seenVisualEventIds.clear();
    this.knownUnlocks = new Set(Object.entries(snapshot.unlocked).filter(([, unlocked]) => unlocked).map(([id]) => id));
    this.lastWaveIndex = snapshot.nextWaveIndex;
    this.lastKillCount = snapshot.counters.kills;
    this.lastLives = snapshot.lives;
  }

  setPaused(paused: boolean): void {
    this.paused = paused;
    if (paused) this.stopOneShots();
    this.syncMusic();
  }

  getSettings(): Readonly<AudioSettings> { return this.settings; }

  toggleMusic(): Readonly<AudioSettings> {
    this.settings = { ...this.settings, musicMuted: !this.settings.musicMuted };
    this.persistSettings();
    this.syncMusic();
    return this.settings;
  }

  toggleSfx(): Readonly<AudioSettings> {
    this.settings = { ...this.settings, sfxMuted: !this.settings.sfxMuted };
    if (this.settings.sfxMuted) this.stopOneShots();
    this.persistSettings();
    return this.settings;
  }

  play(cue: AudioCue): void {
    if (!this.unlocked || this.settings.sfxMuted || this.paused || document.hidden || typeof Audio === 'undefined') return;
    const now = performance.now();
    const cooldown = CUE_COOLDOWNS[cue] ?? 0;
    if (now - (this.lastCueAt.get(cue) ?? -Infinity) < cooldown) return;
    this.lastCueAt.set(cue, now);
    const sound = new Audio(CUE_PATHS[cue]);
    sound.preload = 'auto';
    sound.volume = cue === 'nuclear-explosion' || cue === 'big-explosion' ? 0.28 : cue === 'pop-dead' ? 0.12 : 0.22;
    this.activeOneShots.add(sound);
    let cleaned = false;
    const cleanup = (): void => {
      if (cleaned) return;
      cleaned = true;
      this.activeOneShots.delete(sound);
      sound.src = '';
    };
    sound.addEventListener('ended', cleanup, { once: true });
    sound.addEventListener('error', cleanup, { once: true });
    void sound.play().catch(cleanup);
  }

  sync(snapshot: SimulationSnapshot): void {
    if (this.lastWaveIndex !== null && snapshot.nextWaveIndex > this.lastWaveIndex) this.play('switch');
    if (this.lastKillCount !== null && snapshot.counters.kills > this.lastKillCount) this.play('pop-dead');
    if (this.lastLives !== null && snapshot.lives < this.lastLives) this.play('error');
    this.lastWaveIndex = snapshot.nextWaveIndex;
    this.lastKillCount = snapshot.counters.kills;
    this.lastLives = snapshot.lives;
    snapshot.visualEvents.forEach((event) => {
      if (this.seenVisualEventIds.has(event.id)) return;
      this.seenVisualEventIds.add(event.id);
      const cue = cueForVisualEvent(event);
      if (cue) this.play(cue);
    });
    if (this.seenVisualEventIds.size > 128) {
      const retained = new Set(snapshot.visualEvents.map((event) => event.id));
      this.seenVisualEventIds = new Set([...this.seenVisualEventIds].filter((id) => retained.has(id)));
    }

    const currentUnlocks = new Set(Object.entries(snapshot.unlocked).filter(([, unlocked]) => unlocked).map(([id]) => id));
    if (this.knownUnlocks && [...currentUnlocks].some((id) => !this.knownUnlocks?.has(id))) this.play('quest');
    this.knownUnlocks = currentUnlocks;
  }

  private syncMusic(): void {
    if (!this.unlocked || this.settings.musicMuted || this.paused || document.hidden || typeof Audio === 'undefined') {
      this.music?.pause();
      return;
    }
    if (!this.music) {
      this.music = new Audio(MUSIC_PATH);
      this.music.loop = true;
      this.music.preload = 'auto';
      this.music.volume = 0.18;
    }
    void this.music.play().catch(() => { /* A browser can still reject a stale gesture; the next gesture retries. */ });
  }

  private stopOneShots(): void {
    this.activeOneShots.forEach((sound) => {
      sound.pause();
      try { sound.currentTime = 0; } catch { /* Some browsers reject seeking an unloaded clip. */ }
      sound.src = '';
    });
    this.activeOneShots.clear();
  }

  private persistSettings(): void {
    try { window.localStorage.setItem(AUDIO_STORAGE_KEY, JSON.stringify(this.settings)); } catch { /* Preferences are optional. */ }
  }
}
